import { useEffect, useState } from "react";

/**
 * DEFAULT_MOD_KEY is the modifier shown before the platform is known: the static
 * markup is prerendered under Node, where there is no user agent to read, and the
 * first client render has to match it. Every trigger starts here and refines after
 * mount, so the shortcut hint never causes a hydration mismatch.
 */
const DEFAULT_MOD_KEY = "Ctrl";

/**
 * APPLE_PLATFORM matches the user agents whose shortcut modifier is the command
 * glyph rather than the control key.
 */
const APPLE_PLATFORM = /Mac|iPhone|iPad|iPod/;

/**
 * resolveModKey maps a user agent to the modifier its platform uses for the
 * command-palette shortcut: the command glyph on Apple devices, the control key
 * everywhere else.
 */
export const resolveModKey = (userAgent: string): string =>
  APPLE_PLATFORM.test(userAgent) ? "⌘" : DEFAULT_MOD_KEY;

/**
 * useModKey is the shortcut modifier every search trigger labels itself with. It
 * resolves after mount rather than during render so the prerendered markup and the
 * first client render agree before the platform refines it, and it lives here so
 * the triggers in the header and on the not-found page show one hint from one rule.
 */
export const useModKey = (): string => {
  const [modKey, setModKey] = useState(DEFAULT_MOD_KEY);

  useEffect(() => {
    setModKey(resolveModKey(navigator.userAgent));
  }, []);

  return modKey;
};
